import dayjs from 'dayjs';
import type { ValidationLineInput } from '@/api/handlers/resources/journal';
import type {
    JournalEntryWriteRequest,
    JournalLineWrite,
} from '@/api/handlers/resources/journal/requests';
import type { JournalEntry } from '@/api/models';
import { parseGelToTetri } from '@/pages/accounting/books/chartOfAccounts/utils';

/**
 * Editable draft shapes for the Journal Entry form (logic layer). Amounts are
 * kept as raw decimal strings so an empty field is representable and the tetri
 * parse happens in exactly one place (`parseGelToTetri`).
 */
export type LineDraft = {
    /** Stable row key (never the array index) so focus survives add/remove. */
    key: string;
    accountId: string | null;
    debit: string;
    credit: string;
    memo: string;
};

export type JournalFormState = {
    date: string;
    description: string;
    documentRef: string;
    lines: LineDraft[];
};

/** A client-side UUID for offline-first, idempotent create/save. */
export const newEntryId = (): string =>
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `je-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

export const todayIso = (): string => dayjs().format('YYYY-MM-DD');

export const makeBlankLine = (key: string): LineDraft => ({
    key,
    accountId: null,
    debit: '',
    credit: '',
    memo: '',
});

/** A fresh entry starts with two blank lines (the minimum for a balanced pair). */
export const makeInitialState = (makeKey: () => string): JournalFormState => ({
    date: todayIso(),
    description: '',
    documentRef: '',
    lines: [makeBlankLine(makeKey()), makeBlankLine(makeKey())],
});

/** Hydrate the editor from an existing draft (edit mode). */
export const fromEntry = (
    entry: JournalEntry,
    makeKey: () => string,
): JournalFormState => ({
    date: entry.date,
    description: entry.description,
    documentRef: entry.documentRef,
    lines: entry.lines.map((line) => ({
        key: makeKey(),
        accountId: line.accountId || null,
        debit: line.debitTetri > 0 ? line.debit : '',
        credit: line.creditTetri > 0 ? line.credit : '',
        memo: line.memo,
    })),
});

export const isEmptyLine = (line: LineDraft): boolean =>
    !line.accountId && line.debit.trim() === '' && line.credit.trim() === '';

export type LineParse = {
    debitTetri: number | null;
    creditTetri: number | null;
    /** Non-null when the field held unparseable / sub-tetri / negative input. */
    debitError: 'invalid' | 'sub-tetri' | 'negative' | null;
    creditError: 'invalid' | 'sub-tetri' | 'negative' | null;
};

const sideError = (
    result: ReturnType<typeof parseGelToTetri>,
): 'invalid' | 'sub-tetri' | 'negative' | null => {
    if (result.ok || result.reason === 'empty') {
        return null;
    }
    return result.reason;
};

export const parseLine = (line: LineDraft): LineParse => {
    const debit = parseGelToTetri(line.debit);
    const credit = parseGelToTetri(line.credit);
    return {
        debitTetri: debit.ok ? debit.tetri : null,
        creditTetri: credit.ok ? credit.tetri : null,
        debitError: sideError(debit),
        creditError: sideError(credit),
    };
};

/** Map a row to the validity engine's input (empty/invalid side -> null). */
export const toValidationInput = (line: LineDraft): ValidationLineInput => {
    const parsed = parseLine(line);
    return {
        accountId: line.accountId,
        debitTetri: parsed.debitTetri,
        creditTetri: parsed.creditTetri,
    };
};

/**
 * Build the write payload — fully-empty rows are dropped, amounts converted to
 * integer tetri. Never includes `source` (server-assigned).
 *
 * **This is the money crossing for the write path**, and the only one: it runs
 * each side through `parseLine` (which is `parseGelToTetri` underneath, reading
 * digit positions rather than calling `parseFloat`) so nothing downstream ever
 * sees the user's typed string again.
 *
 * An unparseable side becomes `0`. That is safe rather than lossy because the
 * form refuses to submit while any amount fails to parse — `hasParseErrors`
 * closes both Save and Post, and the offending field shows its own error.
 */
export const toWriteRequest = (
    id: string,
    state: JournalFormState,
): JournalEntryWriteRequest => ({
    id,
    date: state.date,
    description: state.description.trim(),
    documentRef: state.documentRef.trim(),
    lines: state.lines
        .filter((line) => !isEmptyLine(line))
        .map<JournalLineWrite>((line) => {
            const parsed = parseLine(line);
            return {
                accountId: line.accountId,
                debitTetri: parsed.debitTetri ?? 0,
                creditTetri: parsed.creditTetri ?? 0,
                memo: line.memo.trim(),
            };
        }),
});

/** True when the form holds anything worth saving as a draft. */
export const hasSavableContent = (state: JournalFormState): boolean =>
    state.lines.some((line) => !isEmptyLine(line)) ||
    state.description.trim() !== '' ||
    state.documentRef.trim() !== '';
